import { AlertTriangleIcon, BanIcon, CheckIcon, ClockIcon, FileTextIcon, HourglassIcon, LoaderIcon, XIcon } from 'lucide-react'
import React from 'react'

import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { Badge } from 'slab'

type FinancialApplicationState =
  | 'unsubmitted'
  | 'submitted'
  | 'pending_review'
  | 'awaiting_documents'
  | 'approved'
  | 'rejected'
  | 'errored'
  | 'cancelled'

interface FinancialApplicationStateBadgeProps {
  state: string
}

const FinancialApplicationStateBadge: React.FC<FinancialApplicationStateBadgeProps> = ({ state }) => {
  const getBadgeType = (state: FinancialApplicationState) => {
    switch (state) {
      case 'unsubmitted':
        return 'slate'
      case 'submitted':
        return 'info'
      case 'pending_review':
        return 'caution'
      case 'awaiting_documents':
        return 'warning'
      case 'approved':
        return 'success'
      case 'rejected':
        return 'critical'
      case 'errored':
        return 'critical'
      case 'cancelled':
        return 'neutral'
      default:
        return 'info'
    }
  }

  const getBadgeIcon = (state: FinancialApplicationState) => {
    switch (state) {
      case 'unsubmitted':
        return FileTextIcon
      case 'submitted':
        return ClockIcon
      case 'pending_review':
        return HourglassIcon
      case 'awaiting_documents':
        return AlertTriangleIcon
      case 'approved':
        return CheckIcon
      case 'rejected':
        return XIcon
      case 'errored':
        return LoaderIcon
      case 'cancelled':
        return BanIcon
      default:
        return FileTextIcon
    }
  }

  const getDisplayState = (state: FinancialApplicationState) => {
    const labels: Record<FinancialApplicationState, string> = {
      unsubmitted: 'Unsubmitted',
      submitted: 'Submitted',
      pending_review: 'Pending Review',
      awaiting_documents: 'Awaiting Docs',
      approved: 'Approved',
      rejected: 'Rejected',
      errored: 'Errored',
      cancelled: 'Cancelled',
    }
    return labels[state] || state
  }

  const getTooltipMessage = (state: FinancialApplicationState) => {
    switch (state) {
      case 'unsubmitted':
        return 'Application has not yet been submitted to the provider.'
      case 'submitted':
        return 'Application has been submitted and is awaiting processing.'
      case 'pending_review':
        return 'Application is currently under review by the provider.'
      case 'awaiting_documents':
        return 'Provider is waiting for additional documentation.'
      case 'approved':
        return 'Application has been approved by the provider.'
      case 'rejected':
        return 'Application was rejected by the provider.'
      case 'errored':
        return 'An error occurred while processing the application.'
      case 'cancelled':
        return 'Application has been cancelled.'
      default:
        return 'Unknown application state'
    }
  }

  return (
    <DisplayTooltipOnHover text={getTooltipMessage(state as FinancialApplicationState)} asChild>
      <Badge
        type={getBadgeType(state as FinancialApplicationState)}
        leftIcon={getBadgeIcon(state as FinancialApplicationState)}
      >
        {getDisplayState(state as FinancialApplicationState)}
      </Badge>
    </DisplayTooltipOnHover>
  )
}

export default FinancialApplicationStateBadge
